/**
 * Rollbar Provider
 *
 * Implements error tracking using Rollbar.
 * Requires the `rollbar` package to be installed in the consuming app.
 */

import type {
  ErrorTrackingProvider,
  ProviderConfig,
  ErrorContext,
  LogLevel,
} from '../types';
import {
  shouldIgnoreMessage,
  shouldIgnoreReactHookFrameworkBug,
  shouldIgnoreStripeElementTeardown,
  shouldIgnoreBrowserExtensionFrame,
  type RollbarFrame,
} from './ignore-rules';
import { PII_SCRUB_FIELDS, redactRollbarPayload } from './pii-scrub';

// Dynamically import Rollbar to allow optional dependency.
// Rollbar 3.x exports the class as the module's default export.
type RollbarCtor = (typeof import('rollbar'))['default'];
type RollbarInstance = InstanceType<RollbarCtor>;

let Rollbar: RollbarCtor | null = null;
let rollbarInstance: RollbarInstance | null = null;

async function loadRollbar() {
  if (Rollbar) return Rollbar;
  try {
    const mod = await import('rollbar');
    Rollbar = mod.default;
    return Rollbar;
  } catch {
    console.warn(
      '[ErrorTracking] rollbar not installed, falling back to console'
    );
    return null;
  }
}

export class RollbarProvider implements ErrorTrackingProvider {
  private config: ProviderConfig | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  init(config: ProviderConfig): void {
    this.config = config;

    // Don't initialize in development unless explicitly enabled
    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development' &&
      !config.enableInDev
    ) {
      console.log('[ErrorTracking] Rollbar disabled in development');
      return;
    }

    // Initialize asynchronously
    this.initPromise = this.initAsync();
  }

  private async initAsync(): Promise<void> {
    if (!this.config) return;

    const RollbarClass = await loadRollbar();
    if (!RollbarClass) {
      this.initialized = false;
      return;
    }

    const version = this.config.version;

    rollbarInstance = new RollbarClass({
      accessToken: this.config.projectId, // projectId is the access token for Rollbar
      captureUncaught: true,
      captureUnhandledRejections: true,
      environment:
        this.config.environment ||
        process.env.NEXT_PUBLIC_VERCEL_ENV ||
        process.env.NODE_ENV,
      codeVersion: version,
      // Suppress known noise classes (Twilio SDK, Safari network aborts,
      // Next.js framework control-flow throws, browser quirks). See
      // shouldIgnoreMessage in ignore-rules.ts for the full list, plus three
      // stack-aware filters that only drop when the stack is 100% framework /
      // third-party internals (no apps/<app>/src frame):
      // shouldIgnoreReactHookFrameworkBug for the upstream React 19 hook-chain
      // unwind #310 (facebook/react #33580), shouldIgnoreStripeElementTeardown
      // for the benign Stripe Elements iframe-teardown IntegrationError
      // (Rollbar item 1777367786), and shouldIgnoreBrowserExtensionFrame for
      // WebKit-masked browser-extension/userscript noise (Rollbar item 686).
      checkIgnore: (
        _isUncaught: boolean,
        args: unknown[],
        item: Record<string, unknown>,
      ) => {
        const messages: string[] = [];
        for (const arg of args) {
          messages.push(
            typeof arg === 'string'
              ? arg
              : arg instanceof Error
                ? arg.message
                : '',
          );
        }
        for (const msg of messages) {
          // 'client': this provider filters the BROWSER channel, so it also
          // applies the browser-SDK-only rules (both Twilio rules). The server
          // channel deliberately does not -- see #8121 and the `IgnoreChannel`
          // docblock in ignore-rules.ts.
          if (shouldIgnoreMessage(msg, 'client')) return true;
        }
        // Stack-aware filter for the upstream React 19 hook-chain unwind
        // bug. Rollbar's payload nests the trace under
        // body.trace.frames (single-trace) or body.trace_chain[].frames
        // (chained). Inspect both shapes; either may carry the #310.
        const body = item?.body as
          | {
              trace?: { frames?: unknown };
              trace_chain?: Array<{ frames?: unknown }>;
            }
          | undefined;
        const traceFrames: RollbarFrame[] = [];
        const single = body?.trace?.frames;
        if (Array.isArray(single)) {
          traceFrames.push(...(single as RollbarFrame[]));
        }
        if (Array.isArray(body?.trace_chain)) {
          for (const t of body.trace_chain) {
            if (Array.isArray(t?.frames)) {
              traceFrames.push(...(t.frames as RollbarFrame[]));
            }
          }
        }
        if (traceFrames.length === 0) return false;
        // Frame-only check (no message dependency), so it runs once per item
        // rather than once per message like the two filters below it.
        if (shouldIgnoreBrowserExtensionFrame(traceFrames)) return true;
        for (const msg of messages) {
          if (shouldIgnoreReactHookFrameworkBug(msg, traceFrames)) return true;
          // Stack-aware filter for the benign Stripe Elements iframe-teardown
          // IntegrationError (Rollbar item 1777367786) when the stack is 100%
          // Stripe.js + framework internals. Client-only: this is a browser
          // Stripe error, the server path never sees it. See
          // shouldIgnoreStripeElementTeardown in ignore-rules.ts.
          if (shouldIgnoreStripeElementTeardown(msg, traceFrames)) return true;
        }
        return false;
      },
      scrubFields: [...PII_SCRUB_FIELDS],
      transform: (data: Record<string, unknown>) => {
        redactRollbarPayload(data);
        // Browser SDK (rollbar v3.1.0) merges payload.client.javascript.code_version
        // into data.client.javascript.code_version but never sets the top-level
        // data.code_version field. Server/react-native transforms.js do this; the
        // browser transforms.js does not. Without the top-level field, Rollbar's
        // sourcemap matcher and verify-rollbar-sourcemaps.sh both report null.
        // See #3298 for the API-level inspection that proved this gap.
        if (version) {
          data.code_version = version;
        }
      },
      payload: {
        client: {
          javascript: {
            source_map_enabled: true,
            code_version: version,
          },
        },
        custom: {
          service: this.config.serviceName,
        },
      },
    });

    this.initialized = true;
    console.log('[ErrorTracking] Rollbar initialized');
  }

  async captureError(error: Error | string, context?: ErrorContext): Promise<void> {
    // Wait for initialization if in progress
    if (this.initPromise) {
      await this.initPromise;
    }

    if (!this.initialized || !rollbarInstance) {
      // Fallback to console
      console.error('[ErrorTracking] Error (Rollbar not ready):', error, context);
      return;
    }

    const errorObj = typeof error === 'string' ? new Error(error) : error;

    // Build custom data payload
    const custom: Record<string, unknown> = {};
    if (this.config?.serviceName) custom.service = this.config.serviceName;
    if (context?.extra) {
      Object.entries(context.extra).forEach(([k, v]) => {
        custom[k] = v;
      });
    }
    if (context?.tags) {
      Object.entries(context.tags).forEach(([k, v]) => {
        custom[k] = v;
      });
    }
    if (context?.route) custom.route = context.route;
    if (context?.method) custom.method = context.method;
    if (context?.statusCode) custom.statusCode = context.statusCode;

    rollbarInstance.error(errorObj, custom);
  }

  async captureMessage(
    message: string,
    level: LogLevel,
    context?: ErrorContext
  ): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }

    if (!this.initialized || !rollbarInstance) {
      const logFn =
        level === 'error'
          ? console.error
          : level === 'warning'
            ? console.warn
            : console.log;
      logFn(`[ErrorTracking] ${message}`, context);
      return;
    }

    // Build custom data payload
    const custom: Record<string, unknown> = {};
    if (this.config?.serviceName) custom.service = this.config.serviceName;
    if (context?.extra) {
      Object.entries(context.extra).forEach(([k, v]) => {
        custom[k] = v;
      });
    }
    if (context?.tags) {
      Object.entries(context.tags).forEach(([k, v]) => {
        custom[k] = v;
      });
    }

    // Map level to Rollbar method
    switch (level) {
      case 'error':
        rollbarInstance.error(message, custom);
        break;
      case 'warning':
        rollbarInstance.warning(message, custom);
        break;
      default:
        rollbarInstance.info(message, custom);
        break;
    }
  }

  async setUser(userId: string | null): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }

    if (!this.initialized || !rollbarInstance) {
      console.log('[ErrorTracking] User context (Rollbar not ready):', userId);
      return;
    }

    if (userId) {
      rollbarInstance.configure({
        payload: {
          person: {
            id: userId,
          },
        },
      });
    } else {
      rollbarInstance.configure({
        payload: {
          person: undefined,
        },
      });
    }
  }

  async addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, unknown>
  ): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }

    if (!this.initialized || !rollbarInstance) {
      console.log('[ErrorTracking] Breadcrumb (Rollbar not ready):', {
        category,
        message,
        data,
      });
      return;
    }

    // Rollbar uses log for breadcrumbs/debug info
    rollbarInstance.log(`[${category}] ${message}`, data || {});
  }

  isActive(): boolean {
    return this.initialized;
  }

  async flush(timeoutMs = 3000): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }
    if (!this.initialized || !rollbarInstance) return;
    return new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, timeoutMs);
      rollbarInstance!.wait(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  getRollbarInstance(): RollbarInstance | null {
    return rollbarInstance;
  }
}

// Export singleton for use with React Provider
let providerInstance: RollbarProvider | null = null;

export function getRollbarProvider(): RollbarProvider {
  if (!providerInstance) {
    providerInstance = new RollbarProvider();
  }
  return providerInstance;
}
